/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from "react";

/**
 * Shared, injected view of the component registry for renderers that need to
 * render CHILD components (record bodies, split sections, …).
 *
 * Why injection instead of an import: componentRegistry imports those block
 * renderers, so importing the registry back from a block would create a module
 * cycle AND drag the full renderer graph (recharts included) into every leaf
 * consumer. Instead, componentRegistry calls {@link setSharedComponentRegistry}
 * once at its own module init; any app that renders pages (always via
 * PageRenderer → registry) therefore has the registry in place before the
 * first render. When it is absent (leaf unit tests), consumers render nothing
 * for child components — the tolerant fallback.
 */
export type SharedComponentRenderer = (component: any) => JSX.Element | null;

let sharedRegistry: Record<string, SharedComponentRenderer> | null = null;

/** Called once by componentRegistry after the registry object is built. */
export function setSharedComponentRegistry(registry: Record<string, SharedComponentRenderer>): void {
  sharedRegistry = registry;
}

/** The injected registry, or `null` before injection (leaf unit tests). */
export function getSharedComponentRegistry(): Record<string, SharedComponentRenderer> | null {
  return sharedRegistry;
}
